/**
 * Server state for build step 2 — tasks, the Work list, Today per role, the day plan, the Review queue and
 * Dropped tasks. Every read and write goes through a contract route (packages/contracts routes.step2).
 *
 * One rule for writes: any task change can move a row on every work screen (Today, My day, Work, Team plan,
 * Review queue, Dropped, the menu badges), so every write refreshes all of them. A 412 (someone else saved
 * the task first) reloads too, so the next try carries the current version.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  DayPlan,
  ListTasksQuery,
  ListWorkQuery,
  RouteQuery,
  Task,
  TimesheetDay,
  WorkItem,
} from '@amplispot/contracts';
import { ApiFailure, call } from '../api';

export const wkeys = {
  all: ['work'] as const,
  list: (q: object) => ['work', 'list', q] as const,
  whose: (clientId?: string) => ['work', 'whose', clientId ?? ''] as const,
  task: (id: string) => ['work', 'task', id] as const,
  audit: (id: string) => ['work', 'task', id, 'audit'] as const,
  tasks: (q: object) => ['work', 'tasks', q] as const,
  today: ['work', 'today'] as const,
  meToday: ['work', 'me-today'] as const,
  plan: (userId?: string, date?: string) => ['work', 'plan', userId ?? 'me', date ?? ''] as const,
  review: ['work', 'review'] as const,
  dropped: ['work', 'dropped'] as const,
  timesheet: (date: string) => ['work', 'timesheet', date] as const,
};

/** Everything a task write can change: all work screens, the menu badges (GET /me) and client counts. */
export function refreshAfterWrite(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: wkeys.all });
  void qc.invalidateQueries({ queryKey: ['me'] });
  void qc.invalidateQueries({ queryKey: ['clients'] });
}

// ---------------------------------------------------------------- paging

type Paged<T> = { data: T[]; next_cursor: string | null };

/** Follows `next_cursor` to the end for lists a screen needs whole (to group, count or lane them). */
export async function allPages<T>(fetchPage: (cursor: string | undefined) => Promise<Paged<T>>): Promise<T[]> {
  const out: T[] = [];
  let cursor: string | undefined;
  do {
    const page = await fetchPage(cursor);
    out.push(...page.data);
    cursor = page.next_cursor ?? undefined;
  } while (cursor);
  return out;
}

// ---------------------------------------------------------------- reads

/**
 * GET /work, page by page: "Show more" fetches ONE more page (useInfiniteQuery keeps the pages already loaded);
 * nothing is cut off silently — `more` says another page exists. A filter change is a new key, so it starts at
 * page one. After a write every loaded page is refetched, so rows that moved stay right.
 */
export function useWorkList(q: ListWorkQuery) {
  const query = useInfiniteQuery({
    queryKey: wkeys.list(q),
    queryFn: ({ pageParam }) => call('work.list', { query: { ...q, limit: 200, cursor: pageParam } as RouteQuery<'work.list'> }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: (prev) => prev,
  });
  const items: WorkItem[] = query.data?.pages.flatMap((p) => p.data) ?? [];
  return { ...query, items, more: query.hasNextPage };
}

/** GET /work, every page — for a screen that needs a filtered list whole (one client's open work). */
export function useAllWork(q: ListWorkQuery) {
  return useQuery({
    queryKey: [...wkeys.list(q), 'all'],
    queryFn: () => allPages<WorkItem>((cursor) => call('work.list', { query: { ...q, limit: 200, cursor } as RouteQuery<'work.list'> })),
  });
}

export function useWhose(enabled: boolean, clientId?: string) {
  return useQuery({
    queryKey: wkeys.whose(clientId),
    queryFn: () => call('work.whose', { query: clientId ? { client_id: clientId } : {} }),
    enabled,
  });
}

/** GET /tasks, every page — for the Team plan lanes and the week strip on My day. */
export function useAllTasks(q: ListTasksQuery, enabled = true) {
  return useQuery({
    queryKey: wkeys.tasks(q),
    queryFn: () => allPages<Task>((cursor) => call('tasks.list', { query: { ...q, limit: 200, cursor } as RouteQuery<'tasks.list'> })),
    enabled,
  });
}

export function useTask(id: string | null) {
  return useQuery({
    queryKey: wkeys.task(id ?? ''),
    queryFn: () => call('tasks.get', { params: { id: id! } }),
    enabled: !!id,
    retry: (n, e) => !(e instanceof ApiFailure && (e.status === 404 || e.status === 403)) && n < 2,
  });
}

/** GET /audit/tasks/{id} — the task's whole audit trail (every page, newest first from the API), shown oldest first. */
export function useTaskAudit(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: wkeys.audit(id ?? ''),
    queryFn: async () =>
      (
        await allPages((cursor) =>
          call('audit.rowHistory', { params: { table: 'tasks', row_id: id! }, query: { limit: 200, cursor } as RouteQuery<'audit.rowHistory'> }),
        )
      ).reverse(),
    enabled: !!id && enabled,
  });
}

export const useToday = (enabled = true) => useQuery({ queryKey: wkeys.today, queryFn: () => call('today.get'), enabled });
export const useMeToday = (enabled = true) => useQuery({ queryKey: wkeys.meToday, queryFn: () => call('me.today'), enabled });

export function usePlan(userId?: string, enabled = true) {
  return useQuery({
    queryKey: wkeys.plan(userId),
    queryFn: () => call('me.plan', { query: userId ? { user_id: userId } : {} }),
    enabled,
  });
}

export function useReviewQueue() {
  return useQuery({
    queryKey: wkeys.review,
    queryFn: () => allPages((cursor) => call('reviewQueue.list', { query: { limit: 200, cursor } as RouteQuery<'reviewQueue.list'> })),
  });
}

export function useDropped() {
  return useQuery({
    queryKey: wkeys.dropped,
    queryFn: () => allPages((cursor) => call('dropped.list', { query: { limit: 200, cursor } as RouteQuery<'dropped.list'> })),
  });
}

export function useMyTimesheet(date: string, enabled: boolean) {
  return useQuery({
    queryKey: wkeys.timesheet(date),
    queryFn: (): Promise<TimesheetDay> => call('me.timesheet', { query: { date } }),
    enabled,
  });
}

// ---------------------------------------------------------------- writes

/**
 * Any work write. `fn` makes the contract call; success refreshes every work screen. A 412 or 409 also
 * refreshes, so what is on screen (and its version) is current before the person tries again.
 */
export function useWorkWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => refreshAfterWrite(qc),
    onError: (e) => {
      if (e instanceof ApiFailure && (e.status === 412 || e.status === 409)) refreshAfterWrite(qc);
    },
  });
}

/** Saving a slot changes the plan in place (no list refresh needed beyond the plan and Today). */
export function usePlanSave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slots: Record<string, { start_minute: number; minutes: number }>): Promise<DayPlan> =>
      call('me.planSave', { body: { slots } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work', 'plan'] });
      void qc.invalidateQueries({ queryKey: wkeys.meToday });
    },
  });
}

// ---------------------------------------------------------------- shared helpers

/** Days from `fromIso` to `toIso` (business dates, no timezone shift). */
export function daysBetween(fromIso: string, toIso: string): number {
  const a = Date.UTC(+fromIso.slice(0, 4), +fromIso.slice(5, 7) - 1, +fromIso.slice(8, 10));
  const b = Date.UTC(+toIso.slice(0, 4), +toIso.slice(5, 7) - 1, +toIso.slice(8, 10));
  return Math.round((b - a) / 86_400_000);
}

/** `iso` plus `n` days, as YYYY-MM-DD. */
export function addDays(iso: string, n: number): string {
  const d = new Date(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10) + n));
  return d.toISOString().slice(0, 10);
}

/** Prototype due chip (dueLabel/dueCls): "Overdue 2d" (cold) / "Today" (warm) / "Tomorrow" / "In N days" at any distance. */
export function dueText(due: string, today: string): { label: string; tone: 'cold' | 'warm' | '' } {
  const d = daysBetween(today, due);
  if (d < 0) return { label: `Overdue ${-d}d`, tone: 'cold' };
  if (d === 0) return { label: 'Today', tone: 'warm' };
  if (d === 1) return { label: 'Tomorrow', tone: '' };
  return { label: `In ${d} days`, tone: '' };
}

/** "9:30 am" from minutes after midnight. */
export function clock(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
}

/** "1h", "45 min", "1h 30m". */
export function duration(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}
